/**
 * Aug 2026 — GLOBAL technician availability tick-grid.
 *
 * One row per technician, tick the working days. Every ticked day is a
 * fixed 8:00am–2:00pm window that repeats forward automatically (untick
 * to stop). Saves instantly on each tick.
 *
 * GLOBAL: there is ONE availability pool shared by every property
 * campaign — this same grid is rendered on /admin/availability AND on
 * every campaign page, and both write to the same weekly rules, so it
 * never needs setting campaign-by-campaign.
 */
import { useEffect, useState } from "react";
import { Repeat } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { RosterGuardrailDialog } from "@/components/admin/RosterGuardrailDialog";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const FIXED_START = "08:00";
const FIXED_END = "14:00";

export function TechnicianWeekGrid() {
  const [techs, setTechs] = useState([]);
  const [busy, setBusy] = useState("");
  const [guardrail, setGuardrail] = useState(null); // {tid, days, warning}
  const load = () => api.get("/admin/availability/technician-week")
    .then(({ data }) => setTechs(data.technicians || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const _save = async (t, days, confirm, nextEnabled, wd) => {
    setBusy(`${t.technician_id}-${wd}`);
    try {
      const { data } = await api.put(`/admin/availability/technician-week/${t.technician_id}`, { days, confirm });
      toast.success(nextEnabled
        ? `${DAYS[wd]}s on for ${t.name} — ${data.slots_generated ?? 0} future slots added`
        : `${DAYS[wd]}s off for ${t.name} — future unbooked slots removed`);
      setGuardrail(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.requires_confirmation) {
        setGuardrail({ t, days, warning: detail });
      } else {
        toast.error(detail || "Save failed");
        load();
      }
    } finally { setBusy(""); }
  };

  const toggle = (t, wd) => {
    const cur = t.days[wd] || t.days[String(wd)] || {};
    const nextEnabled = !cur.enabled;
    setTechs((all) => all.map((x) => x.technician_id === t.technician_id
      ? { ...x, days: { ...x.days, [String(wd)]: { ...cur, enabled: nextEnabled, start_time: FIXED_START, end_time: FIXED_END } } }
      : x));
    const days = {};
    for (let d = 0; d < 7; d++) {
      const src = t.days[d] || t.days[String(d)] || {};
      const enabled = d === wd ? nextEnabled : !!src.enabled;
      days[String(d)] = { enabled, start_time: FIXED_START, end_time: FIXED_END };
    }
    _save(t, days, false, nextEnabled, wd);
  };

  return (
    <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="tech-week-panel">
      <RosterGuardrailDialog
        warning={guardrail?.warning}
        busy={!!busy}
        onCancel={() => { setGuardrail(null); load(); }}
        onConfirm={() => guardrail && _save(guardrail.t, guardrail.days, true, null, null)} />
      <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
        <Repeat className="w-4 h-4" /> Technician availability
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800">Global — all campaigns</span>
      </h2>
      <p className="text-xs text-slate-500 mt-1">
        Tick the days each technician works. Every ticked day is <b>8:00am – 2:00pm</b> and keeps
        repeating week after week until you untick it. One shared pool — every campaign and every
        property QR code books from it, so set it once here and it applies everywhere. Changes save
        instantly; booked appointments are never removed.
      </p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="text-left text-[11px] uppercase tracking-wider text-slate-400 font-bold pb-2 pr-4">Technician</th>
              {DAY_SHORT.map((d, i) => (
                <th key={d} className={`text-center text-[11px] uppercase tracking-wider font-bold pb-2 px-2 ${i >= 5 ? "text-slate-300" : "text-slate-400"}`}>{d}</th>
              ))}
              <th className="text-right text-[11px] uppercase tracking-wider text-slate-400 font-bold pb-2 pl-2">Days</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {techs.map((t) => {
              const enabledCount = Object.values(t.days).filter((d) => d.enabled).length;
              return (
                <tr key={t.technician_id} data-testid={`tech-week-row-${t.technician_id}`}>
                  <td className="py-2.5 pr-4 font-semibold text-slate-800 whitespace-nowrap">{t.name}</td>
                  {DAY_SHORT.map((_, wd) => {
                    const d = t.days[wd] || t.days[String(wd)] || {};
                    const saving = busy === `${t.technician_id}-${wd}`;
                    return (
                      <td key={wd} className="text-center px-2">
                        <input
                          type="checkbox"
                          checked={!!d.enabled}
                          disabled={!!busy}
                          data-testid={`tech-week-day-${t.technician_id}-${wd}`}
                          onChange={() => toggle(t, wd)}
                          className={`w-5 h-5 accent-cyan-600 cursor-pointer ${saving ? "opacity-40" : ""}`}
                        />
                      </td>
                    );
                  })}
                  <td className="py-2.5 pl-2 text-right text-xs text-slate-400 whitespace-nowrap" data-testid={`tech-week-count-${t.technician_id}`}>
                    {enabledCount ? `${enabledCount} × 8am–2pm` : "off"}
                  </td>
                </tr>
              );
            })}
            {techs.length === 0 && (
              <tr><td colSpan={9} className="py-4 text-sm text-slate-400">
                No active technicians yet — <a href="/field/technicians" className="underline font-semibold">add technicians</a> first.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
